export type ContiguousProgramStepGroup<
  T extends { groupTitle?: string | null; groupId?: string | null },
> = {
  /** Clé logique : `__ungrouped__`, trim du titre, ou `gid:…` */
  key: string;
  /** Libellé après trim (null = sans groupe) */
  label: string | null;
  items: T[];
  /** Index de la première étape dans le tableau d’origine */
  startIndex: number;
};

/** Environnements sans lib DOM (build schemas) : pas de type `Crypto` global. */
type WebCryptoWithRandomUUID = { randomUUID?: () => string };

const newGroupId = (): string => {
  const cryptoObj = (globalThis as { crypto?: WebCryptoWithRandomUUID }).crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `g_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

/** Identifiant stable pour un bloc de groupe (éditeur + persistance). */
export const newProgramGroupId = (): string => {
  return newGroupId();
};

/**
 * Découpe les étapes en blocs **contigus**.
 * Si `groupId` est renseigné sur une étape, la frontière de bloc suit cet id ;
 * sinon (données anciennes ou sans id), on retombe sur le trim de `groupTitle`.
 */
export const groupStepsByContiguousGroupTitle = <
  T extends { groupTitle?: string | null; groupId?: string | null },
>(
  steps: readonly T[],
): ContiguousProgramStepGroup<T>[] => {
  const groups: ContiguousProgramStepGroup<T>[] = [];
  let i = 0;
  while (i < steps.length) {
    const startIndex = i;
    const first = steps[i]!;
    const gid = first.groupId?.trim() || null;
    const items: T[] = [];
    let j = i;
    if (gid) {
      while (j < steps.length) {
        const s = steps[j]!;
        const sg = s.groupId?.trim() || null;
        if (sg !== gid) break;
        items.push(s);
        j += 1;
      }
      const normalized = first.groupTitle?.trim() || null;
      groups.push({
        key: `gid:${gid}`,
        label: normalized,
        items,
        startIndex,
      });
    } else {
      const normalized = first.groupTitle?.trim() || null;
      const key = normalized ?? "__ungrouped__";
      while (j < steps.length) {
        const s = steps[j]!;
        if (s.groupId?.trim()) break;
        const n = s.groupTitle?.trim() || null;
        const k = n ?? "__ungrouped__";
        if (k !== key) break;
        items.push(s);
        j += 1;
      }
      groups.push({
        key,
        label: normalized,
        items,
        startIndex,
      });
    }
    i = j;
  }
  return groups;
};

/**
 * Attribue un `groupId` commun à chaque bloc contigu (ou conserve l’id existant).
 */
export const normalizeProgramStepGroupIds = <
  T extends { groupTitle?: string | null; groupId?: string | null },
>(
  steps: readonly T[],
): Array<T & { groupId: string }> => {
  const groups = groupStepsByContiguousGroupTitle(steps);
  const out: Array<T & { groupId: string }> = [];
  for (const g of groups) {
    const id =
      (g.items[0] as { groupId?: string | null }).groupId?.trim() ||
      newGroupId();
    for (const item of g.items) {
      out.push({ ...(item as T), groupId: id });
    }
  }
  return out;
};

/**
 * Détecte si le même nom de groupe (insensible à la casse) apparaît dans
 * plusieurs blocs (selon `groupStepsByContiguousGroupTitle`).
 */
export const findFirstDuplicateProgramGroupTitle = (
  steps: ReadonlyArray<{
    groupTitle?: string | null;
    groupId?: string | null;
  }>,
): string | null => {
  const groups = groupStepsByContiguousGroupTitle(steps);
  const seen = new Set<string>();
  for (const g of groups) {
    const title = (g.items[0]?.groupTitle ?? "").trim();
    if (!title) continue;
    const k = title.toLowerCase();
    if (seen.has(k)) {
      return title;
    }
    seen.add(k);
  }
  return null;
};
